/*
 * @Author: along longwang6@163.com
 * @Date: 2026-04-24 19:30:25
 * @LastEditors: along longwang6@163.com
 * @LastEditTime: 2026-05-19 15:01:41
 * @FilePath: /ehr-web/src/components/TabBar/index.tsx
 * @Description: 这是默认设置,请设置`customMade`, 打开koroFileHeader查看配置 进行设置: https://github.com/OBKoro1/koro1FileHeader/wiki/%E9%85%8D%E7%BD%AE
 */
import cs from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tooltip } from '@arco-design/web-react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  Modifier,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import styles from './index.module.less';
import { TabItem } from '@/store/modules/tab';
import { IconClose } from '@arco-design/web-react/icon';

type Props = {
  tabs: TabItem[];
  activeKey: string;
  onSwitch: (tab: TabItem) => void;
  onClose: (tab: TabItem) => void;
  onCloseOthers: (tab: TabItem) => void;
  onCloseRight: (tab: TabItem) => void;
  onReorder: (activeKey: string, overKey: string) => void;
};

const DEFAULT_TITLE_MAX_LENGTH = 20;

const getMiddleEllipsisTitle = (
  title: string,
  maxLength = DEFAULT_TITLE_MAX_LENGTH
) => {
  if (!title) return '';
  if (title.length <= maxLength) return title;

  const suffixLength = Math.max(4, Math.floor(maxLength * 0.35));
  const prefixLength = Math.max(6, maxLength - suffixLength - 1);
  return `${title.slice(0, prefixLength)}…${title.slice(-suffixLength)}`;
};

const restrictToHorizontalAxis: Modifier = ({ transform }) => {
  return {
    ...transform,
    y: 0,
  };
};

const TabBar = ({ tabs, activeKey, onSwitch, onClose, onReorder }: Props) => {
  const navRef = useRef<HTMLDivElement | null>(null);
  const tabNodeMapRef = useRef<Record<string, HTMLDivElement | null>>({});
  const titleNodeMapRef = useRef<Record<string, HTMLSpanElement | null>>({});
  const manualSwitchKeyRef = useRef<string | null>(null);
  const wasDraggingRef = useRef(false);
  const [showLeftMask, setShowLeftMask] = useState(false);
  const [showRightMask, setShowRightMask] = useState(false);
  const [titleOverflowMap, setTitleOverflowMap] = useState<
    Record<string, boolean>
  >({});
  const [scrollHint, setScrollHint] = useState({
    visible: false,
    thumbWidthPct: 0,
    thumbLeftPct: 0,
  });
  const [renderTabs, setRenderTabs] = useState<TabItem[]>(tabs);

  useEffect(() => {
    setRenderTabs(tabs);
  }, [tabs]);

  const updateTitleOverflow = useCallback(() => {
    const nextMap: Record<string, boolean> = {};
    renderTabs.forEach((tab) => {
      const titleNode = titleNodeMapRef.current[tab.key];
      nextMap[tab.key] = Boolean(
        titleNode && titleNode.scrollWidth - titleNode.clientWidth > 1
      );
    });
    setTitleOverflowMap((prev) => {
      const prevKeys = Object.keys(prev);
      const nextKeys = Object.keys(nextMap);
      if (
        prevKeys.length === nextKeys.length &&
        nextKeys.every((key) => prev[key] === nextMap[key])
      ) {
        return prev;
      }
      return nextMap;
    });
  }, [renderTabs]);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 6 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 6 },
    })
  );

  const onScrollRailPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      const nav = navRef.current;
      const rail = e.currentTarget;
      if (!nav) return;

      const maxScrollLeft = nav.scrollWidth - nav.clientWidth;
      if (maxScrollLeft <= 1) return;

      const railRect = rail.getBoundingClientRect();
      const trackW = railRect.width;
      const thumbEl = (e.target as HTMLElement).closest(
        '[data-tab-scroll-thumb]'
      );
      const thumbW = thumbEl
        ? thumbEl.getBoundingClientRect().width
        : (trackW * scrollHint.thumbWidthPct) / 100;
      const movable = Math.max(1, trackW - thumbW);

      if (thumbEl) {
        e.preventDefault();
        rail.setPointerCapture(e.pointerId);
        const startX = e.clientX;
        const startScroll = nav.scrollLeft;
        const k = maxScrollLeft / movable;

        const onMove = (ev: PointerEvent) => {
          const dx = ev.clientX - startX;
          nav.scrollLeft = Math.min(
            maxScrollLeft,
            Math.max(0, startScroll + dx * k)
          );
        };
        const onUp = (ev: PointerEvent) => {
          try {
            rail.releasePointerCapture(ev.pointerId);
          } catch {
            /* ignore */
          }
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
          window.removeEventListener('pointercancel', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onUp);
        return;
      }

      e.preventDefault();
      const x = e.clientX - railRect.left;
      const p = (x - thumbW / 2) / movable;
      nav.scrollLeft = Math.min(maxScrollLeft, Math.max(0, p * maxScrollLeft));
    },
    [scrollHint.thumbWidthPct]
  );

  const updateMask = useCallback(() => {
    const navNode = navRef.current;
    if (!navNode) return;

    const { scrollLeft, scrollWidth, clientWidth } = navNode;
    const maxScrollLeft = scrollWidth - clientWidth;
    const hasOverflow = maxScrollLeft > 1;

    if (!hasOverflow) {
      setShowLeftMask(false);
      setShowRightMask(false);
      setScrollHint({ visible: false, thumbWidthPct: 0, thumbLeftPct: 0 });
      return;
    }

    setShowLeftMask(scrollLeft > 1);
    setShowRightMask(scrollLeft < maxScrollLeft - 1);

    const thumbWidthPct = Math.min(
      100,
      Math.max((clientWidth / scrollWidth) * 100, 6)
    );
    const thumbLeftPct =
      maxScrollLeft <= 0
        ? 0
        : (scrollLeft / maxScrollLeft) * (100 - thumbWidthPct);
    setScrollHint({
      visible: true,
      thumbWidthPct,
      thumbLeftPct,
    });
  }, []);

  useEffect(() => {
    updateMask();
    updateTitleOverflow();
  }, [tabs, updateMask, updateTitleOverflow]);

  useEffect(() => {
    const navNode = navRef.current;
    if (!navNode) return;

    const onResize = () => {
      updateMask();
      updateTitleOverflow();
    };
    window.addEventListener('resize', onResize);
    const RO = typeof ResizeObserver !== 'undefined' ? ResizeObserver : null;
    const ro = RO
      ? new RO(() => {
          updateMask();
          updateTitleOverflow();
        })
      : null;
    if (ro) ro.observe(navNode);
    return () => {
      window.removeEventListener('resize', onResize);
      ro?.disconnect();
    };
  }, [updateMask, updateTitleOverflow]);

  useEffect(() => {
    const rafId = window.requestAnimationFrame(() => {
      const activeTabNode = tabNodeMapRef.current[activeKey];
      if (!activeTabNode) return;

      const behavior =
        manualSwitchKeyRef.current === activeKey ? 'auto' : 'smooth';
      manualSwitchKeyRef.current = null;

      activeTabNode.scrollIntoView({
        behavior,
        block: 'nearest',
        inline: 'center',
      });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [activeKey]);

  useEffect(() => {
    updateTitleOverflow();
  }, [renderTabs, activeKey, updateTitleOverflow]);

  if (!tabs.length) return null;

  const isLastTab = tabs.length <= 1;
  const canCloseTab = (tab: TabItem) => tab.closable && !isLastTab;

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = renderTabs.findIndex((tab) => tab.key === active.id);
    const newIndex = renderTabs.findIndex((tab) => tab.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    wasDraggingRef.current = true;
    setRenderTabs((current) => arrayMove(current, oldIndex, newIndex));
    onReorder(String(active.id), String(over.id));
    window.setTimeout(() => {
      wasDraggingRef.current = false;
    }, 0);
  };

  const SortableTabItem = ({ tab }: { tab: TabItem }) => {
    const shouldShowTitleTip =
      tab.title.length > DEFAULT_TITLE_MAX_LENGTH ||
      Boolean(titleOverflowMap[tab.key]);
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({
      id: tab.key,
      transition: {
        duration: 220,
        easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
      },
    });

    return (
      <div
        ref={(node) => {
          setNodeRef(node);
          tabNodeMapRef.current[tab.key] = node;
        }}
        style={{
          transform: CSS.Transform.toString(
            transform
              ? {
                  ...transform,
                  scaleX: 1,
                  scaleY: 1,
                }
              : null
          ),
          transition,
          zIndex: isDragging ? 10 : undefined,
        }}
        className={cs(styles.tabItem, {
          [styles.tabItemActive]: activeKey === tab.key,
          [styles.tabItemDragging]: isDragging,
        })}
        onClick={() => {
          if (wasDraggingRef.current) return;
          manualSwitchKeyRef.current = tab.key;
          onSwitch(tab);
        }}
        {...attributes}
        {...listeners}
      >
        <Tooltip content={tab.title} disabled={!shouldShowTitleTip}>
          <span
            className={`${styles.title} ${
              canCloseTab(tab) ? styles.titleWithCloseBtn : ''
            }`}
            ref={(node) => {
              titleNodeMapRef.current[tab.key] = node;
            }}
          >
            {getMiddleEllipsisTitle(tab.title)}
          </span>
        </Tooltip>
        {canCloseTab(tab) && (
          <span
            className={styles.closeBtn}
            title="关闭标签"
            onClick={(e) => {
              e.stopPropagation();
              onClose(tab);
            }}
          >
            <IconClose style={{ fontSize: 12 }} />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.tabBarWrap}>
      <>
        <div
          className={cs(styles.edgeMask, styles.leftMask, {
            [styles.edgeMaskVisible]: showLeftMask,
          })}
          title="可继续横向滚动"
        />
        <div
          className={cs(styles.edgeMask, styles.rightMask, {
            [styles.edgeMaskVisible]: showRightMask,
          })}
          title="可继续横向滚动"
        />
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={renderTabs.map((tab) => tab.key)}
            strategy={horizontalListSortingStrategy}
          >
            <div
              ref={navRef}
              className={styles.tabBar_nav}
              onScroll={updateMask}
            >
              {renderTabs.map((tab) => (
                <SortableTabItem key={tab.key} tab={tab} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
        {scrollHint.visible ? (
          <div
            className={styles.scrollRail}
            aria-hidden
            data-testid="tab-bar-scroll-hint"
            onPointerDown={onScrollRailPointerDown}
          >
            <div
              className={styles.scrollThumb}
              data-tab-scroll-thumb=""
              style={{
                width: `${scrollHint.thumbWidthPct}%`,
                left: `${scrollHint.thumbLeftPct}%`,
              }}
            />
          </div>
        ) : null}
      </>
    </div>
  );
};

export default observer(TabBar);
